import { create } from "zustand";
import type {
  ChatAckPayload,
  LiveAssistanceSessionPayload,
  SessionResumePayload,
  SupervisorHandoffPayload,
  SupervisorLiveEndedPayload,
  SupervisorLiveErrorPayload,
  SupervisorLiveMessageForwardedPayload,
  SupervisorLiveMessagePayload,
  SupervisorLiveReceiptPayload,
  SupervisorLiveTypingPayload,
  SupervisorRoutingStartedPayload,
  SupervisorResponseErrorPayload,
  SupervisorStageUpdatedPayload,
} from "./types";

export type SocketTurnStatus = {
  clientMessageId: string;
  acknowledged: boolean;
  routingStarted: boolean;
  stage: string | null;
  stageStatus: string | null;
  error: string | null;
};

export type LiveDeliveryStatus = "forwarded" | "delivered" | "read" | "failed";

export type LiveAssistanceStatus = {
  session: LiveAssistanceSessionPayload | null;
  pendingHandoffClientMessageId: string | null;
  isConnecting: boolean;
  isTyping: boolean;
  typingAgentName: string | null;
  typingStartedAt: Date | null;
  seenEventIds: Set<string>;
  /** Maps provider_message_id → receipt status (delivered/read/failed). */
  receipts: Record<string, string>;
  /** Set of client_message_ids confirmed forwarded to the provider. */
  forwardedMessageIds: Set<string>;
  /** Set of provider_message_ids for outbound student messages. */
  forwardedProviderMessageIds: Set<string>;
  /** Maps provider_message_id → client_message_id for outbound student messages. */
  clientMessageIdByProviderMessageId: Record<string, string>;
  /** Maps client_message_id → provider_message_id for outbound student messages. */
  providerMessageIdByClientMessageId: Record<string, string>;
  /** Maps client_message_id → delivery status for outbound student messages. */
  deliveryStatusByClientMessageId: Record<string, LiveDeliveryStatus>;
  error: string | null;
};

const SAFE_STAGE_STATUSES = new Set(["started", "completed", "failed"]);

type SocketStatusState = {
  activeTurn: SocketTurnStatus | null;
  liveAssistance: LiveAssistanceStatus;
  startTurn: (clientMessageId: string) => void;
  acknowledgeTurn: (payload: ChatAckPayload) => void;
  markRoutingStarted: (payload: SupervisorRoutingStartedPayload) => void;
  updateStage: (payload: SupervisorStageUpdatedPayload) => void;
  failTurn: (payload: SupervisorResponseErrorPayload) => void;
  finishTurn: (clientMessageId?: string) => void;
  applySessionResume: (payload: SessionResumePayload) => void;
  markHandoffStarted: (payload: SupervisorHandoffPayload) => void;
  markHandoffCompleted: (payload: SupervisorHandoffPayload) => boolean;
  markHandoffFailed: (payload: SupervisorHandoffPayload) => boolean;
  appendLiveMessage: (payload: SupervisorLiveMessagePayload) => boolean;
  appendLiveStudentMessage: (payload: { live_session_id?: string; client_message_id?: string }) => void;
  updateLiveTyping: (payload: SupervisorLiveTypingPayload) => void;
  markLiveEnding: () => void;
  markLiveEnded: (payload: SupervisorLiveEndedPayload) => boolean;
  applyLiveReceipt: (payload: SupervisorLiveReceiptPayload) => void;
  markMessageForwarded: (payload: SupervisorLiveMessageForwardedPayload) => void;
  failLiveAssistance: (payload: SupervisorLiveErrorPayload) => boolean;
  reset: () => void;
};

const matchesActiveTurn = (active: SocketTurnStatus | null, clientMessageId?: string) =>
  Boolean(active && (!clientMessageId || active.clientMessageId === clientMessageId));

const isTerminalLiveSession = (session: LiveAssistanceSessionPayload | null | undefined) =>
  session?.state === "ended" || session?.state === "failed";

const matchesLiveSession = (
  status: LiveAssistanceStatus,
  payload: { live_session_id?: string },
) => {
  const activeSessionId = status.session?.live_session_id;
  return Boolean(
    activeSessionId &&
      payload.live_session_id &&
      activeSessionId === payload.live_session_id &&
      !isTerminalLiveSession(status.session),
  );
};

const canApplyLiveEnded = (
  status: LiveAssistanceStatus,
  payload: { live_session_id?: string },
) => {
  if (matchesLiveSession(status, payload)) return true;
  return Boolean(status.session?.live_session_id && !payload.live_session_id && status.session.state === "ending");
};

const matchesPendingHandoff = (status: LiveAssistanceStatus, payload: { client_message_id?: string }) =>
  Boolean(status.pendingHandoffClientMessageId && payload.client_message_id === status.pendingHandoffClientMessageId);

const canApplyHandoffFailure = (status: LiveAssistanceStatus, payload: { client_message_id?: string }) =>
  matchesPendingHandoff(status, payload);

const canApplyHandoffCompletion = (status: LiveAssistanceStatus, payload: { client_message_id?: string }) =>
  matchesPendingHandoff(status, payload);

const emptyLiveAssistance = (): LiveAssistanceStatus => ({
  session: null,
  pendingHandoffClientMessageId: null,
  isConnecting: false,
  isTyping: false,
  typingAgentName: null,
  typingStartedAt: null,
  seenEventIds: new Set(),
  receipts: {},
  forwardedMessageIds: new Set(),
  forwardedProviderMessageIds: new Set(),
  clientMessageIdByProviderMessageId: {},
  providerMessageIdByClientMessageId: {},
  deliveryStatusByClientMessageId: {},
  error: null,
});

const emptyLiveAssistanceWithDeliveryStatus = (
  deliveryStatusByClientMessageId: Record<string, LiveDeliveryStatus>,
): LiveAssistanceStatus => ({
  ...emptyLiveAssistance(),
  deliveryStatusByClientMessageId,
});

const normalizeReceiptStatus = (eventType?: string): LiveDeliveryStatus | null => {
  switch (eventType) {
    case "delivered":
    case "read":
    case "failed":
      return eventType;
    default:
      return null;
  }
};

const deliveryStatusRank: Record<LiveDeliveryStatus, number> = {
  forwarded: 0,
  failed: 1,
  delivered: 2,
  read: 3,
};

const mergeDeliveryStatus = (
  current: LiveDeliveryStatus | undefined,
  next: LiveDeliveryStatus,
): LiveDeliveryStatus => {
  if (!current) return next;
  return deliveryStatusRank[current] >= deliveryStatusRank[next] ? current : next;
};

const SAFE_STAGE_MESSAGES: Record<string, string> = {
  "Checking your request": "Checking your request",
  "Finding the right specialist": "Finding the right specialist",
  "Reviewing sources": "Reviewing sources",
  "Reviewing the answer": "Reviewing the answer",
  "Preparing follow-up options": "Preparing follow-up options",
  "Working on your request": "Working on your request",
  Preparing: "Thinking",
  Rewrite: "Understanding your question",
  Retrieve: "Reviewing sources",
  Compare: "Comparing options",
  Finalize: "Preparing your answer",
};

export const useSocketStatusStore = create<SocketStatusState>((set) => ({
  activeTurn: null,
  liveAssistance: emptyLiveAssistance(),
  startTurn: (clientMessageId) =>
    set({
      activeTurn: {
        clientMessageId,
        acknowledged: false,
        routingStarted: false,
        stage: null,
        stageStatus: null,
        error: null,
      },
    }),
  acknowledgeTurn: (payload) =>
    set((state) => {
      if (!matchesActiveTurn(state.activeTurn, payload.client_message_id)) return state;
      return {
        activeTurn: state.activeTurn ? { ...state.activeTurn, acknowledged: true } : null,
      };
    }),
  markRoutingStarted: (payload) =>
    set((state) => {
      if (!matchesActiveTurn(state.activeTurn, payload.client_message_id)) return state;
      return {
        activeTurn: state.activeTurn ? { ...state.activeTurn, routingStarted: true } : null,
      };
    }),
  updateStage: (payload) =>
    set((state) => {
      if (!matchesActiveTurn(state.activeTurn, payload.client_message_id)) return state;
      return {
        activeTurn: state.activeTurn
          ? {
              ...state.activeTurn,
              stage: payload.stage ?? state.activeTurn.stage,
              stageStatus:
                payload.status && SAFE_STAGE_STATUSES.has(payload.status)
                  ? payload.status
                  : state.activeTurn.stageStatus,
            }
          : null,
      };
    }),
  failTurn: (payload) =>
    set((state) => {
      if (!matchesActiveTurn(state.activeTurn, payload.client_message_id)) return state;
      return {
        activeTurn: state.activeTurn
          ? { ...state.activeTurn, error: payload.error ?? "Backend error" }
          : null,
      };
    }),
  applySessionResume: (payload) =>
    set((state) => {
      // If the backend reports no active live session, clear any stale
      // client-side live assistance state to prevent UI desync.
      if (!payload.live_assistance) {
        return {
          liveAssistance: emptyLiveAssistanceWithDeliveryStatus(
            state.liveAssistance.deliveryStatusByClientMessageId,
          ),
        };
      }
      const isSameLiveSession =
        state.liveAssistance.session?.live_session_id === payload.live_assistance.live_session_id;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          session: payload.live_assistance,
          pendingHandoffClientMessageId: null,
          isConnecting: payload.live_assistance.state === "connecting",
          seenEventIds: isSameLiveSession ? state.liveAssistance.seenEventIds : new Set(),
          receipts: isSameLiveSession ? state.liveAssistance.receipts : {},
          forwardedMessageIds: isSameLiveSession ? state.liveAssistance.forwardedMessageIds : new Set(),
          forwardedProviderMessageIds: isSameLiveSession
            ? state.liveAssistance.forwardedProviderMessageIds
            : new Set(),
          clientMessageIdByProviderMessageId: isSameLiveSession
            ? state.liveAssistance.clientMessageIdByProviderMessageId
            : {},
          providerMessageIdByClientMessageId: isSameLiveSession
            ? state.liveAssistance.providerMessageIdByClientMessageId
            : {},
          deliveryStatusByClientMessageId: state.liveAssistance.deliveryStatusByClientMessageId,
          error: null,
        },
      };
    }),
  markHandoffStarted: (payload) =>
    set((state) => ({
      liveAssistance: {
        ...state.liveAssistance,
        pendingHandoffClientMessageId: payload.client_message_id ?? null,
        isConnecting: true,
        error: null,
      },
    })),
  markHandoffCompleted: (payload) => {
    let accepted = false;
    set((state) => {
      if (!canApplyHandoffCompletion(state.liveAssistance, payload)) return state;
      accepted = true;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          pendingHandoffClientMessageId: null,
          session: payload.live_assistance ?? state.liveAssistance.session,
          isConnecting: false,
          seenEventIds: new Set(),
          receipts: {},
          forwardedMessageIds: new Set(),
          forwardedProviderMessageIds: new Set(),
          clientMessageIdByProviderMessageId: {},
          providerMessageIdByClientMessageId: {},
          deliveryStatusByClientMessageId: state.liveAssistance.deliveryStatusByClientMessageId,
          error: null,
        },
      };
    });
    return accepted;
  },
  markHandoffFailed: (payload) => {
    let accepted = false;
    set((state) => {
      if (!canApplyHandoffFailure(state.liveAssistance, payload)) return state;
      accepted = true;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          pendingHandoffClientMessageId: null,
          isConnecting: false,
          error: payload.reason ?? "Live assistance is not available right now.",
        },
      };
    });
    return accepted;
  },
  appendLiveMessage: (payload) => {
    let accepted = false;
    set((state) => {
      if (!matchesLiveSession(state.liveAssistance, payload)) return state;
      const text = payload.text?.trim();
      if (!text) return state;
      const id = payload.event_id ?? payload.provider_message_id ?? `${payload.live_session_id ?? "live"}-${Date.now()}`;
      if (state.liveAssistance.seenEventIds.has(id)) return state;
      const seenEventIds = new Set(state.liveAssistance.seenEventIds);
      seenEventIds.add(id);
      accepted = true;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          session: mergeLiveSession(state.liveAssistance.session, payload),
          isConnecting: false,
          isTyping: false,
          typingAgentName: null,
          typingStartedAt: null,
          seenEventIds,
          error: null,
        },
      };
    });
    return accepted;
  },
  appendLiveStudentMessage: (payload) =>
    set((state) => {
      if (!matchesLiveSession(state.liveAssistance, payload)) return state;
      const clientMessageId = payload.client_message_id;
      if (!clientMessageId) return { liveAssistance: { ...state.liveAssistance, error: null } };
      const forwardedMessageIds = new Set(state.liveAssistance.forwardedMessageIds);
      forwardedMessageIds.add(clientMessageId);
      return {
        liveAssistance: {
          ...state.liveAssistance,
          forwardedMessageIds,
          deliveryStatusByClientMessageId: {
            ...state.liveAssistance.deliveryStatusByClientMessageId,
            [clientMessageId]: mergeDeliveryStatus(
              state.liveAssistance.deliveryStatusByClientMessageId[clientMessageId],
              "forwarded",
            ),
          },
          error: null,
        },
      };
    }),
  updateLiveTyping: (payload) =>
    set((state) => {
      if (!matchesLiveSession(state.liveAssistance, payload)) return state;
      const isTyping = payload.event_type === "typing_started";
      return {
        liveAssistance: {
          ...state.liveAssistance,
          session: mergeLiveSession(state.liveAssistance.session, payload),
          isTyping,
          typingAgentName: isTyping ? payload.agent?.display_name ?? "Support Specialist" : null,
          typingStartedAt: isTyping ? new Date() : null,
        },
      };
    }),
  markLiveEnding: () =>
    set((state) => ({
        liveAssistance: {
          ...state.liveAssistance,
          session: state.liveAssistance.session
            ? { ...state.liveAssistance.session, state: "ending" }
            : state.liveAssistance.session,
          isTyping: false,
          typingAgentName: null,
          typingStartedAt: null,
        },
    })),
  markLiveEnded: (payload) => {
    let accepted = false;
    set((state) => {
      if (!canApplyLiveEnded(state.liveAssistance, payload)) return state;
      accepted = true;
      const liveSessionId = payload.live_session_id ?? state.liveAssistance.session?.live_session_id;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          session: mergeLiveSession(state.liveAssistance.session, {
            ...payload,
            live_session_id: liveSessionId,
            state: payload.state ?? "ended",
          }),
          isConnecting: false,
          isTyping: false,
          typingAgentName: null,
          typingStartedAt: null,
          error: null,
        },
      };
    });
    return accepted;
  },
  applyLiveReceipt: (payload) =>
    set((state) => {
      if (!matchesLiveSession(state.liveAssistance, payload)) return state;
      const key = payload.provider_message_id ?? payload.event_id;
      if (!key || !payload.event_type) return state;
      const receiptStatus = normalizeReceiptStatus(payload.event_type);
      const clientMessageId = state.liveAssistance.clientMessageIdByProviderMessageId[key];
      return {
        liveAssistance: {
          ...state.liveAssistance,
          receipts: { ...state.liveAssistance.receipts, [key]: payload.event_type },
          deliveryStatusByClientMessageId:
            clientMessageId && receiptStatus
              ? {
                  ...state.liveAssistance.deliveryStatusByClientMessageId,
                  [clientMessageId]: mergeDeliveryStatus(
                    state.liveAssistance.deliveryStatusByClientMessageId[clientMessageId],
                    receiptStatus,
                  ),
                }
              : state.liveAssistance.deliveryStatusByClientMessageId,
        },
      };
    }),
  markMessageForwarded: (payload) =>
    set((state) => {
      if (!matchesLiveSession(state.liveAssistance, payload)) return state;
      const id = payload.client_message_id;
      if (!id) return state;
      const forwardedMessageIds = new Set(state.liveAssistance.forwardedMessageIds);
      forwardedMessageIds.add(id);
      const forwardedProviderMessageIds = new Set(state.liveAssistance.forwardedProviderMessageIds);
      if (payload.provider_message_id) forwardedProviderMessageIds.add(payload.provider_message_id);
      const clientMessageIdByProviderMessageId = { ...state.liveAssistance.clientMessageIdByProviderMessageId };
      const providerMessageIdByClientMessageId = { ...state.liveAssistance.providerMessageIdByClientMessageId };
      if (payload.provider_message_id) {
        clientMessageIdByProviderMessageId[payload.provider_message_id] = id;
        providerMessageIdByClientMessageId[id] = payload.provider_message_id;
      }
      const existingReceipt = payload.provider_message_id
        ? normalizeReceiptStatus(state.liveAssistance.receipts[payload.provider_message_id])
        : null;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          forwardedMessageIds,
          forwardedProviderMessageIds,
          clientMessageIdByProviderMessageId,
          providerMessageIdByClientMessageId,
          deliveryStatusByClientMessageId: {
            ...state.liveAssistance.deliveryStatusByClientMessageId,
            [id]: mergeDeliveryStatus(
              state.liveAssistance.deliveryStatusByClientMessageId[id],
              existingReceipt ?? "forwarded",
            ),
          },
        },
      };
    }),
  failLiveAssistance: (payload) => {
    let accepted = false;
    set((state) => {
      if (!matchesLiveSession(state.liveAssistance, payload)) return state;
      accepted = true;
      return {
        liveAssistance: {
          ...state.liveAssistance,
          isConnecting: false,
          error: payload.error ?? "Live assistance is not available right now.",
        },
      };
    });
    return accepted;
  },
  finishTurn: (clientMessageId) =>
    set((state) => {
      if (!matchesActiveTurn(state.activeTurn, clientMessageId)) return state;
      return { activeTurn: null };
    }),
  reset: () => set({ activeTurn: null, liveAssistance: emptyLiveAssistance() }),
}));

export function getSocketStageMessage(status: SocketTurnStatus | null): string | null {
  if (!status) return null;
  if (status.error) return "Something went wrong.";
  if (!status.stage) return null;
  return SAFE_STAGE_MESSAGES[status.stage] ?? null;
}

export function getLiveAssistanceStatusMessage(status: LiveAssistanceStatus): string | null {
  if (status.error) return status.error;
  if (status.isConnecting) return "Connecting with a support specialist";
  if (status.session?.state === "waiting_for_specialist") return "Waiting for a support specialist";
  if (status.session?.state === "live") {
    const name = status.session.agent?.display_name ?? "Support Specialist";
    return `Live with ${name}`;
  }
  if (status.session?.state === "ending") return "Ending live chat";
  return null;
}

export function getLiveStudentDeliveryStatus(
  status: LiveAssistanceStatus,
  clientMessageId?: string | null,
): LiveDeliveryStatus | null {
  if (!clientMessageId) return null;
  return status.deliveryStatusByClientMessageId[clientMessageId] ?? null;
}

export function getLiveStudentDeliveryMessage(
  status: LiveAssistanceStatus,
  clientMessageId?: string | null,
): string | null {
  switch (getLiveStudentDeliveryStatus(status, clientMessageId)) {
    case "forwarded":
      return "Sent";
    case "delivered":
      return "Delivered";
    case "read":
      return "Read";
    case "failed":
      return "Delivery failed";
    default:
      return null;
  }
}

function mergeLiveSession(
  session: LiveAssistanceSessionPayload | null,
  payload: { live_session_id?: string; provider?: string; state?: string; agent?: LiveAssistanceSessionPayload["agent"] },
): LiveAssistanceSessionPayload | null {
  if (!session && !payload.live_session_id) return null;
  return {
    ...session,
    live_session_id: payload.live_session_id ?? session?.live_session_id,
    provider: payload.provider ?? session?.provider,
    state: payload.state ?? session?.state,
    agent: payload.agent ?? session?.agent,
    updated_at: new Date().toISOString(),
  };
}

export const socketStatusActions = {
  startTurn: (clientMessageId: string) => useSocketStatusStore.getState().startTurn(clientMessageId),
  acknowledgeTurn: (payload: ChatAckPayload) => useSocketStatusStore.getState().acknowledgeTurn(payload),
  markRoutingStarted: (payload: SupervisorRoutingStartedPayload) =>
    useSocketStatusStore.getState().markRoutingStarted(payload),
  updateStage: (payload: SupervisorStageUpdatedPayload) => useSocketStatusStore.getState().updateStage(payload),
  failTurn: (payload: SupervisorResponseErrorPayload) => useSocketStatusStore.getState().failTurn(payload),
  finishTurn: (clientMessageId?: string) => useSocketStatusStore.getState().finishTurn(clientMessageId),
  applySessionResume: (payload: SessionResumePayload) => useSocketStatusStore.getState().applySessionResume(payload),
  markHandoffStarted: (payload: SupervisorHandoffPayload) =>
    useSocketStatusStore.getState().markHandoffStarted(payload),
  markHandoffCompleted: (payload: SupervisorHandoffPayload) =>
    useSocketStatusStore.getState().markHandoffCompleted(payload),
  markHandoffFailed: (payload: SupervisorHandoffPayload) =>
    useSocketStatusStore.getState().markHandoffFailed(payload),
  appendLiveMessage: (payload: SupervisorLiveMessagePayload) =>
    useSocketStatusStore.getState().appendLiveMessage(payload),
  appendLiveStudentMessage: (payload: { live_session_id?: string; client_message_id?: string }) =>
    useSocketStatusStore.getState().appendLiveStudentMessage(payload),
  updateLiveTyping: (payload: SupervisorLiveTypingPayload) =>
    useSocketStatusStore.getState().updateLiveTyping(payload),
  markLiveEnding: () => useSocketStatusStore.getState().markLiveEnding(),
  markLiveEnded: (payload: SupervisorLiveEndedPayload) => useSocketStatusStore.getState().markLiveEnded(payload),
  applyLiveReceipt: (payload: SupervisorLiveReceiptPayload) =>
    useSocketStatusStore.getState().applyLiveReceipt(payload),
  markMessageForwarded: (payload: SupervisorLiveMessageForwardedPayload) =>
    useSocketStatusStore.getState().markMessageForwarded(payload),
  failLiveAssistance: (payload: SupervisorLiveErrorPayload) =>
    useSocketStatusStore.getState().failLiveAssistance(payload),
  reset: () => useSocketStatusStore.getState().reset(),
};
